/**
 * Full-screen overlay for recording voice reply to a family member.
 * Uses MediaRecorder API. Uploads to voice-notes worker on stop.
 */

import { FC, useCallback, useEffect, useRef, useState } from "react";
import { uploadReply } from "../../services/voice-notes-api";
import "./recording-overlay.scss";

interface RecordingOverlayProps {
  recipientName: string;
  onComplete: (success: boolean) => void;
  onCancel: () => void;
}

const RecordingOverlay: FC<RecordingOverlayProps> = ({
  recipientName,
  onComplete,
  onCancel,
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Start recording on mount
  useEffect(() => {
    let stream: MediaStream | null = null;

    async function startRecording() {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const recorder = new MediaRecorder(stream, {
          mimeType: MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
            ? "audio/webm;codecs=opus"
            : "audio/webm",
        });
        recorderRef.current = recorder;
        chunksRef.current = [];

        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) chunksRef.current.push(e.data);
        };

        recorder.onstop = async () => {
          const blob = new Blob(chunksRef.current, { type: "audio/webm" });
          const success = await uploadReply(blob, recipientName);
          onComplete(success);
        };

        recorder.start();
        setIsRecording(true);

        timerRef.current = setInterval(() => {
          setSeconds((s) => s + 1);
        }, 1000);
      } catch {
        onCancel();
      }
    }

    startRecording();

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (stream) stream.getTracks().forEach((t) => t.stop());
    };
  }, [recipientName, onComplete, onCancel]);

  const handleStop = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (recorderRef.current?.state === "recording") {
      recorderRef.current.stop();
    }
    setIsRecording(false);
  }, []);

  const formatTime = (s: number) =>
    `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;

  return (
    <div className="recording-overlay">
      <div className="recording-overlay__content">
        <p className="recording-overlay__label">
          {isRecording
            ? `Запись ответа для ${recipientName}...`
            : "Отправка..."}
        </p>
        <p className="recording-overlay__timer">{formatTime(seconds)}</p>
        {isRecording && (
          <button className="recording-overlay__stop" onClick={handleStop}>
            <svg viewBox="0 0 24 24" width="64" height="64">
              <rect
                x="6"
                y="6"
                width="12"
                height="12"
                rx="2"
                fill="currentColor"
              />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

export default RecordingOverlay;
